import { useAtom, useAtomValue } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { surveyGroupFilterAtom } from '~/atoms'
import { Box } from '~/components/elements/Box'
import { HStack } from '~/components/elements/HStack'
import { Typography } from '~/components/elements/Typography'
import { VStack } from '~/components/elements/VStack'
import { AnalysisCommentBlock } from '~/components/features/AnalysisCommentBlock'
import {
  ShareDropDownMenuButton,
  type ShareDropDownMenuButtonProps,
} from '~/components/features/ShareDropDownMenuButton'
import {
  buildSurveyGroupFilterTextGroups,
  compact,
  dataDisplayTypeLabelI18nKeyMap,
  exists,
} from '~/utils'

import {
  commentAtom,
  dataDisplayTypeAtom,
  isChartMaxUnifiedAtom,
  savedCommentAtom,
  tableChartTypeAtom,
} from '../../atoms'
import { useRestoringParams } from '../../hooks/restoringParams/useRestoringParams'
import { useBrandAccountId } from '../../hooks/useBrandAccountId'
import { useCurrentSurveyGroup } from '../../hooks/useCurrentSurveyGroup'
import {
  indicatorLabelI18nKeyMap,
  type IndicatorWithCustomized,
  isIndicatorWithDistribution,
  tableChartTypeLabelMap,
} from '../../utils'

export type HeadingContainerProps = React.PropsWithChildren<{
  indicator: IndicatorWithCustomized
  isPercentageOnly?: boolean
  label: string
}>

export const HeadingContainer = ({
  children,
  indicator,
  isPercentageOnly = false,
  label,
}: HeadingContainerProps) => {
  const surveyGroup = useCurrentSurveyGroup()
  const brandAccountId = useBrandAccountId()
  const surveyGroupFilter = useAtomValue(surveyGroupFilterAtom)

  const tableChartType = useAtomValue(tableChartTypeAtom)
  const dataDisplayType = useAtomValue(dataDisplayTypeAtom)
  const isChartMaxUnified = useAtomValue(isChartMaxUnifiedAtom)

  const { t } = useTranslation()

  const [commentRecord, setCommentRecord] = useAtom(commentAtom)
  const comment = commentRecord[indicator] ?? ''
  const setComment = React.useCallback(
    (next: string) =>
      setCommentRecord((prev) => ({ ...prev, [indicator]: next })),
    [setCommentRecord, indicator],
  )
  const [savedCommentRecord, setSavedCommentRecord] = useAtom(savedCommentAtom)
  const savedComment = savedCommentRecord[indicator] ?? ''
  const setSavedComment = React.useCallback(
    (next: string) =>
      setSavedCommentRecord((prev) => ({ ...prev, [indicator]: next })),
    [setSavedCommentRecord, indicator],
  )
  const [image, serImage] = React.useState<HTMLDivElement | null>(null)
  const restoringParams = useRestoringParams(indicator)
  const shareDropDownMenuButtonProps = React.useMemo<
    ShareDropDownMenuButtonProps<string>
  >(
    () => ({
      downloadAsPng: exists(image) ? { image, name: indicator } : undefined,
      getSharedUrl: { restoringParams },
      saveDocumentImage: exists(image)
        ? {
            figureInfo: {
              brandAccountId,
              pathKey: 'timeSeriesAnalysis',
              figureType: compact([
                isIndicatorWithDistribution(indicator)
                  ? indicatorLabelI18nKeyMap[indicator]
                  : label,
                indicator !== 'distribution' &&
                  tableChartTypeLabelMap[tableChartType],
              ]).join(' - '),
              surveyGroup: surveyGroup.title,
              tableChartType: tableChartTypeLabelMap[tableChartType],
              dataDisplayType: dataDisplayTypeLabelI18nKeyMap[dataDisplayType],
              isValueAxisMaxStandardized: isChartMaxUnified ? 'ON' : 'OFF',
              surveyGroupFilter: buildSurveyGroupFilterTextGroups({
                filterGroups: surveyGroupFilter?.filterGroups ?? [],
                questions: surveyGroup.questions,
                customerAttributeGroups: surveyGroup.customerAttributeGroups,
                brands: surveyGroup.brands,
                t,
              }),
            },
            commentReadOnly: true,
            image,
            initialComment: comment,
            onSaved: (saved) => setSavedComment(saved),
            restoringParams,
          }
        : undefined,
    }),
    [
      brandAccountId,
      comment,
      dataDisplayType,
      image,
      indicator,
      isChartMaxUnified,
      label,
      restoringParams,
      setSavedComment,
      surveyGroup,
      surveyGroupFilter?.filterGroups,
      tableChartType,
      t,
    ],
  )

  return (
    <Box id={indicator}>
      <HStack align="center" spacing={2}>
        <Typography fontWeight={400} variant="heading2">
          {label}
        </Typography>

        <HStack spacing={2}>
          <Typography
            color={
              isPercentageOnly || dataDisplayType === 'percentage'
                ? 'normal'
                : 'lighter'
            }
            variant="small"
          >
            {t(dataDisplayTypeLabelI18nKeyMap.percentage)}
          </Typography>

          {!isPercentageOnly && (
            <Typography
              color={
                dataDisplayType === 'estimatedPopulation' ? 'normal' : 'lighter'
              }
              variant="small"
            >
              {t(dataDisplayTypeLabelI18nKeyMap.estimatedPopulation)}{' '}
              {t('in_thousands')}
            </Typography>
          )}
        </HStack>
      </HStack>

      <AnalysisCommentBlock
        savedComment={savedComment}
        saveDocumentImage={shareDropDownMenuButtonProps.saveDocumentImage}
        value={comment}
        onCommentChange={setComment}
      />

      <Box
        horizontalAppearance={
          indicator === 'distribution' ? 'default' : 'fit-content'
        }
      >
        <Box padding={[2, 0]}>
          <VStack spacing={1}>
            <HStack justify="end" spacing={0}>
              <ShareDropDownMenuButton {...shareDropDownMenuButtonProps} />
            </HStack>

            <Box ref={serImage}>{children}</Box>
          </VStack>
        </Box>
      </Box>
    </Box>
  )
}
